import { SECURITY_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { securityDetailsPath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';

import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { CURRENCIES_GROUPED_OPTIONS } from '@dealstack/frontend/shared/currencies';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	drawerTransitionProps,
	groupedSelectDividerStyles,
} from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { DataSecurityTct } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Drawer, Flex } from '@mantine/core';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
	EDIT_SECURITY_FORM_FIELD,
	useEditSecurityDrawerController,
} from './useEditSecurityDrawerController';

interface Props {
	className?: string;
	opened: boolean;
	onClose: () => void;
	security?: DataSecurityTct;
	dealId: string;
	onSaved: () => void;
	/**
	 * Whether this viewer may save. Which role that means is the caller's to decide — the endpoint
	 * accepts a deal editor or a programme one, and only the page knows which it is showing.
	 */
	canEdit: boolean;
	/** Whether this viewer may delete. Needs `onDelete` as well to render anything. */
	canDelete?: boolean;
	/** Renders a delete action; the caller owns the confirmation flow. */
	onDelete?: (securityId: string) => void;
}

const EditSecurityDrawer = ({
	opened,
	security,
	dealId,
	onClose,
	onSaved,
	canEdit,
	canDelete = false,
	onDelete,
	className,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const { updateSecurityRequest, editSecurityForm, onSubmit } = useEditSecurityDrawerController({
		security,
		opened,
		onSaved,
		onClose,
	});

	const securityId = security?.id;
	// generatePath silently drops empty segments, so only offer the link once every param is known.
	const detailsPath =
		dealId && security?.issuerId && securityId
			? securityDetailsPath({ dealId, personId: security.issuerId, securityId })
			: undefined;

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			className={className}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={tCapManage('securities.drawer.edit_title')}
		>
			{updateSecurityRequest.isPending && <InlineLoader />}
			<FormProvider {...editSecurityForm}>
				<form onSubmit={editSecurityForm.handleSubmit(onSubmit)}>
					<Select
						name={EDIT_SECURITY_FORM_FIELD.type}
						control={editSecurityForm.control}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						label={tCapManage('securities.drawer.type.label')}
						placeholder={tCapManage('securities.drawer.type.placeholder')}
						data={SECURITY_TYPE_OPTIONS}
						disabled={!canEdit}
					/>
					<TextInput
						name={EDIT_SECURITY_FORM_FIELD.name}
						control={editSecurityForm.control}
						size="md"
						label={tCapManage('securities.drawer.name.label')}
						placeholder={tCapManage('securities.drawer.name.placeholder')}
						data-testid={TEST_ELEMENT_IDS.SECURITY_EDIT_DRAWER_NAME_INPUT}
						disabled={!canEdit}
					/>
					<Select
						name={EDIT_SECURITY_FORM_FIELD.currency}
						control={editSecurityForm.control}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						label={tCapManage('securities.drawer.currency.label')}
						placeholder={tCapManage('securities.drawer.currency.placeholder')}
						data={CURRENCIES_GROUPED_OPTIONS}
						disabled
						styles={groupedSelectDividerStyles}
					/>

					<Flex direction="column" gap="0.5rem" mt="auto">
						{canEdit && (
							<Button
								type="submit"
								fullWidth
								data-testid={TEST_ELEMENT_IDS.SECURITY_EDIT_DRAWER_SAVE_BUTTON}
								disabled={updateSecurityRequest.isPending}
							>
								{tCapManage('securities.drawer.save_button')}
							</Button>
						)}
						{detailsPath && (
							<Button
								component={Link}
								to={detailsPath}
								fullWidth
								variant="default"
								data-testid={TEST_ELEMENT_IDS.SECURITY_EDIT_DRAWER_VIEW_MORE_BUTTON}
							>
								{tCapManage('securities.drawer.view_more_button')}
							</Button>
						)}
						{onDelete && securityId && canDelete && (
							<Button
								type="button"
								fullWidth
								variant="subtle"
								color="red"
								data-testid={TEST_ELEMENT_IDS.SECURITY_EDIT_DRAWER_DELETE_BUTTON}
								disabled={updateSecurityRequest.isPending}
								onClick={() => onDelete(securityId)}
							>
								{tCapManage('securities.drawer.delete')}
							</Button>
						)}
					</Flex>
				</form>
			</FormProvider>
		</Drawer>
	);
};

const Memoized = memo(EditSecurityDrawer);
const Styled = styled(Memoized)`
	form {
		height: 100%;
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
`;

export { Styled as EditSecurityDrawer };
